/**
 * Transaction Progress Component
 *
 * แสดงสถานะย่อยแบบ step-by-step visualization
 * - Progress Steps สำหรับ Withdraw flow
 * - Error/Success messages
 * - Timestamps
 */

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  Circle,
  XCircle,
  Loader2,
  AlertTriangle,
  Clock,
  ArrowRight,
} from "lucide-react";

// ============================================
// Types
// ============================================

export interface TransactionProgressProps {
  type: "Deposit" | "Withdraw";
  status: string;
  isCreditWithdraw?: string;
  isCashWithdraw?: string;
  withdrawBy?: string;
  isCreditWithdrawAmount?: number;
  isCashWithdrawAmount?: number;
  statusNote?: string;
  apiBankObj?:
    | {
        name?: string;
        status?: string;
        notes?: string | { note?: string };
        imagePath?: string;
        creditRefunded?: boolean;
      }
    | {
        name: string;
        status: string;
        notes: string;
        creditRefunded?: boolean;
      }
    | undefined;
  agentLog?: any;
  errorMessage?: string;
  creditDeductedAt?: string;
  approvedAt?: string;
  completedAt?: string;
  rejectedAt?: string;
  cancelledAt?: string;
  creditRefundedAt?: string;
  cancellationReason?: string;
  rejectionReason?: string;
  promotionInfo?: {
    code?: string;
    max_withdraw?: number;
    amount_credit?: number;
    amount_cash?: number;
  }; // ✅ ข้อมูลโปรโมชั่น
  createdBy?: string; // ✅ ผู้สร้างรายการ
  updatedBy?: string; // ✅ ผู้แก้ไขรายการล่าสุด
  createdAt?: string; // ✅ วันที่สร้าง
  updatedAt?: string; // ✅ วันที่แก้ไขล่าสุด
}

type StepStatus = "pending" | "processing" | "success" | "error" | "skipped";

interface Step {
  id: string;
  label: string;
  status: StepStatus;
  detail?: string;
  timestamp?: string;
  amount?: number;
}

// ============================================
// Helper Functions
// ============================================

function getStepIcon(status: StepStatus, isSmall = false) {
  const size = isSmall ? "w-3.5 h-3.5" : "w-4 h-4";

  switch (status) {
    case "success":
      return <CheckCircle2 className={cn(size, "text-emerald-500")} />;
    case "error":
      return <XCircle className={cn(size, "text-red-500")} />;
    case "processing":
      return <Loader2 className={cn(size, "text-blue-500 animate-spin")} />;
    case "skipped":
      return <Circle className={cn(size, "text-gray-300")} />;
    default:
      return <Circle className={cn(size, "text-gray-400")} />;
  }
}

function getStepBgColor(status: StepStatus) {
  switch (status) {
    case "success":
      return "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800";
    case "error":
      return "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800";
    case "processing":
      return "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800";
    default:
      return "bg-muted/30 border-border";
  }
}

// ============================================
// Withdraw Progress Steps
// ============================================

function getWithdrawSteps(props: TransactionProgressProps): Step[] {
  const credit = String(props.isCreditWithdraw ?? 0);
  const cash = String(props.isCashWithdraw ?? 0);
  const isManual =
    props.withdrawBy === "Manaul" || props.withdrawBy === "Manual";
  const isCancelled = props.status === "Cancelled";
  const isRejected = props.status === "Rejected";
  const hasCreditRefund =
    props.creditRefundedAt ||
    (props.apiBankObj && "creditRefunded" in props.apiBankObj
      ? props.apiBankObj.creditRefunded
      : false);

  const steps: Step[] = [];

  // Step 1: ตัดเครดิต
  let creditStatus: StepStatus = "pending";
  let creditDetail = "รอตัดเครดิตจากเกม";

  if (hasCreditRefund) {
    // ถ้าคืนเครดิตแล้ว แสดงว่าตัดสำเร็จแล้วคืน
    creditStatus = "success";
    creditDetail = "ตัดเครดิตสำเร็จ (คืนแล้ว)";
  } else if (credit === "1") {
    creditStatus = "success";
    creditDetail = "ตัดเครดิตสำเร็จ";
  } else if (credit === "2") {
    creditStatus = "processing";
    creditDetail = "กำลังตัดเครดิต...";
  } else if (credit === "3") {
    creditStatus = "error";
    creditDetail = "ตัดเครดิตไม่สำเร็จ";
  }

  steps.push({
    id: "credit",
    label: "ตัดเครดิต",
    status: creditStatus,
    detail: creditDetail,
    amount: props.isCreditWithdrawAmount,
    timestamp: props.creditDeductedAt,
  });

  // Step 2: โอนเงิน (ถ้าตัดเครดิตสำเร็จ)
  if (credit === "1" || credit === "2" || credit === "3" || hasCreditRefund) {
    let cashStatus: StepStatus = "pending";
    let cashDetail = isManual
      ? "รอ Admin โอนเงินด้วยตนเอง"
      : "รอโอนเงินอัตโนมัติ";

    if (cash === "1") {
      cashStatus = "success";
      cashDetail = isManual ? "Admin โอนเงินสำเร็จ" : "โอนเงินอัตโนมัติสำเร็จ";
    } else if (cash === "2") {
      cashStatus = "processing";
      cashDetail = isManual ? "รอ Admin ยืนยันการโอน" : "กำลังโอนเงิน...";
    } else if (cash === "3") {
      cashStatus = "error";
      cashDetail = isManual
        ? "การโอนด้วยตนเองล้มเหลว"
        : "โอนเงินอัตโนมัติไม่สำเร็จ";
    }

    // ถ้ายกเลิก/ปฏิเสธ และยังไม่โอนเงิน
    if ((isCancelled || isRejected) && cash !== "1") {
      cashStatus = "error";
      cashDetail = isCancelled ? "ยกเลิกการโอน" : "ปฏิเสธการโอน";
    }

    // ถ้าตัดเครดิตยังไม่สำเร็จ และไม่ใช่กรณี refund ให้ cash step เป็น skipped
    if (credit !== "1" && !hasCreditRefund) {
      cashStatus = "skipped";
      cashDetail = "รอตัดเครดิตก่อน";
    }

    steps.push({
      id: "cash",
      label: isManual ? "โอนเงิน (Manual)" : "โอนเงิน (Auto)",
      status: cashStatus,
      detail: cashDetail,
      amount: props.isCashWithdrawAmount,
      timestamp: props.completedAt,
    });
  }

  // Step 3: คืนเครดิต (ถ้ามี)
  if (hasCreditRefund) {
    steps.push({
      id: "refund",
      label: "คืนเครดิต",
      status: "success",
      detail: "คืนเครดิตให้สมาชิกแล้ว",
      amount: props.isCreditWithdrawAmount,
      timestamp: props.creditRefundedAt,
    });
  }

  return steps;
}

// ============================================
// Deposit Progress Steps
// ============================================

function getDepositSteps(props: TransactionProgressProps): Step[] {
  const steps: Step[] = [];

  // Step 1: รับแจ้ง
  steps.push({
    id: "received",
    label: "รับแจ้งฝาก",
    status: "success",
    detail: "ได้รับคำขอฝากเงิน",
  });

  // Step 2: ตรวจสอบ
  let verifyStatus: StepStatus = "processing";
  let verifyDetail = "กำลังตรวจสอบข้อมูล";

  if (props.status === "Approved" || props.status === "Completed") {
    verifyStatus = "success";
    verifyDetail = "ตรวจสอบสำเร็จ";
  } else if (props.status === "Rejected") {
    verifyStatus = "error";
    verifyDetail = props.statusNote || "ไม่ผ่านการตรวจสอบ";
  }

  steps.push({
    id: "verify",
    label: "ตรวจสอบ",
    status: verifyStatus,
    detail: verifyDetail,
  });

  // Step 3: เติมเครดิต
  if (verifyStatus === "success") {
    steps.push({
      id: "credit",
      label: "เติมเครดิต",
      status: "success",
      detail: "เติมเครดิตสำเร็จ",
    });
  }

  return steps;
}

// ============================================
// Main Component
// ============================================

export function TransactionProgress(props: TransactionProgressProps) {
  const steps =
    props.type === "Withdraw"
      ? getWithdrawSteps(props)
      : getDepositSteps(props);

  const hasError = steps.some((s) => s.status === "error");
  const isProcessing = steps.some((s) => s.status === "processing");
  const allSuccess = steps.every(
    (s) => s.status === "success" || s.status === "skipped"
  );

  // Error/Status Note
  const errorNote =
    props.errorMessage ||
    props.statusNote ||
    (props.apiBankObj?.notes && typeof props.apiBankObj.notes === "object"
      ? props.apiBankObj.notes.note
      : typeof props.apiBankObj?.notes === "string"
      ? props.apiBankObj.notes
      : undefined);

  return (
    <div className="space-y-3">
      {/* ✅ Promotion Info Banner - แสดงชัดเจนสำหรับ Manual Withdraw */}
      {props.promotionInfo && (
        <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">
                  ⚠️ ลูกค้าติดโปรโมชัน: {props.promotionInfo.code}
                </span>
              </div>
              <div className="text-xs text-yellow-700 dark:text-yellow-400 space-y-1">
                <div className="flex justify-between items-center">
                  <span>ตัดเครดิต:</span>
                  <span className="font-mono font-semibold">
                    {props.promotionInfo.amount_credit?.toLocaleString()} บาท
                  </span>
                </div>
                <div className="flex justify-between items-center pt-1 border-t border-yellow-300 dark:border-yellow-700">
                  <span className="font-semibold text-yellow-900 dark:text-yellow-200">
                    💰 เงินที่ต้องโอนให้ลูกค้า:
                  </span>
                  <span className="font-mono font-bold text-yellow-900 dark:text-yellow-200 text-sm">
                    {props.promotionInfo.amount_cash?.toLocaleString()} บาท
                  </span>
                </div>
                {props.promotionInfo.max_withdraw && (
                  <div className="text-[10px] text-yellow-600 dark:text-yellow-500 pt-0.5">
                    (เงื่อนไข: ถอนได้ไม่เกิน{" "}
                    {props.promotionInfo.max_withdraw.toLocaleString()} บาท)
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Progress Steps - Horizontal */}
      <div className="flex items-center gap-2">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center gap-2 flex-1">
            {/* Step */}
            <div
              className={cn(
                "flex-1 p-2.5 rounded-lg border transition-all",
                getStepBgColor(step.status)
              )}
            >
              <div className="flex items-center gap-2">
                {getStepIcon(step.status)}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate">
                    {step.label}
                  </div>
                  <div
                    className={cn(
                      "text-[10px] truncate",
                      step.status === "error"
                        ? "text-red-600"
                        : step.status === "success"
                        ? "text-emerald-600"
                        : step.status === "processing"
                        ? "text-blue-600"
                        : "text-muted-foreground"
                    )}
                  >
                    {step.detail}
                  </div>
                </div>
                {step.amount && step.status === "success" && (
                  <Badge
                    variant="outline"
                    className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-mono"
                  >
                    ฿{step.amount.toLocaleString()}
                  </Badge>
                )}
              </div>
              {step.timestamp && (
                <div className="text-[9px] text-muted-foreground mt-1 flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5" />
                  {new Date(step.timestamp).toLocaleString("th-TH", {
                    hour: "2-digit",
                    minute: "2-digit",
                    day: "2-digit",
                    month: "2-digit",
                  })}
                </div>
              )}
            </div>

            {/* Arrow between steps */}
            {index < steps.length - 1 && (
              <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            )}
          </div>
        ))}
      </div>

      {/* Error/Note Message */}
      {errorNote && (
        <div
          className={cn(
            "p-3 rounded-lg border text-sm flex items-start gap-2",
            hasError
              ? "bg-red-50 dark:bg-red-950/30 border-red-200 text-red-700"
              : isProcessing
              ? "bg-blue-50 dark:bg-blue-950/30 border-blue-200 text-blue-700"
              : "bg-muted/30 border-border text-muted-foreground"
          )}
        >
          {hasError ? (
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          ) : (
            <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <div className="font-medium text-xs mb-0.5">
              {hasError ? "ข้อผิดพลาด" : "หมายเหตุ"}
            </div>
            <div className="text-[11px]">{errorNote}</div>
          </div>
        </div>
      )}

      {/* Agent Log (if available) */}
      {props.agentLog && (
        <details className="group">
          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1">
            <span className="group-open:rotate-90 transition-transform">▶</span>
            ดู Agent Log
          </summary>
          <pre className="mt-2 p-2 bg-muted/50 rounded text-[10px] font-mono overflow-auto max-h-32">
            {JSON.stringify(props.agentLog, null, 2)}
          </pre>
        </details>
      )}

      {/* Overall Status Summary */}
      <div className="flex items-center justify-between pt-2 border-t">
        <div className="flex items-center gap-2">
          {allSuccess && (
            <Badge className="bg-emerald-500 text-white text-[10px]">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              เสร็จสิ้น
            </Badge>
          )}
          {hasError && (
            <Badge className="bg-red-500 text-white text-[10px]">
              <XCircle className="w-3 h-3 mr-1" />
              มีข้อผิดพลาด
            </Badge>
          )}
          {isProcessing && !hasError && (
            <Badge className="bg-blue-500 text-white text-[10px]">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              กำลังดำเนินการ
            </Badge>
          )}
          {!allSuccess && !hasError && !isProcessing && (
            <Badge className="bg-amber-500 text-white text-[10px]">
              <Clock className="w-3 h-3 mr-1" />
              รอดำเนินการ
            </Badge>
          )}
        </div>

        {props.withdrawBy && (
          <Badge variant="outline" className="text-[10px]">
            {props.withdrawBy === "Manaul" || props.withdrawBy === "Manual"
              ? "🖐 Manual"
              : "🤖 Auto"}
          </Badge>
        )}
      </div>

      {/* ✅ ข้อมูลผู้กระทำ */}
      {(props.createdBy || props.updatedBy) && (
        <div className="flex items-center gap-4 text-[10px] text-muted-foreground pt-1">
          {props.createdBy && (
            <div className="flex items-center gap-1">
              <span className="font-medium">สร้างโดย:</span>
              <span>{props.createdBy}</span>
              {props.createdAt && (
                <span className="text-[9px] opacity-70">
                  (
                  {new Date(props.createdAt).toLocaleString("th-TH", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  )
                </span>
              )}
            </div>
          )}
          {props.updatedBy && props.updatedBy !== props.createdBy && (
            <div className="flex items-center gap-1">
              <span className="font-medium">แก้ไขล่าสุดโดย:</span>
              <span>{props.updatedBy}</span>
              {props.updatedAt && (
                <span className="text-[9px] opacity-70">
                  (
                  {new Date(props.updatedAt).toLocaleString("th-TH", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  )
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// Compact Version for inline display
// ============================================

export function TransactionProgressCompact(props: TransactionProgressProps) {
  const steps =
    props.type === "Withdraw"
      ? getWithdrawSteps(props)
      : getDepositSteps(props);

  return (
    <div className="flex items-center gap-1">
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-center gap-1">
          <div className="flex items-center gap-0.5">
            {getStepIcon(step.status, true)}
            <span
              className={cn(
                "text-[9px] whitespace-nowrap",
                step.status === "error"
                  ? "text-red-600"
                  : step.status === "success"
                  ? "text-emerald-600"
                  : step.status === "processing"
                  ? "text-blue-600"
                  : "text-muted-foreground"
              )}
            >
              {step.label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <ArrowRight className="w-2.5 h-2.5 text-muted-foreground" />
          )}
        </div>
      ))}
    </div>
  );
}

export default TransactionProgress;
